import React, { useState, useEffect, useCallback } from 'react';
import { eventApi } from '../../../services/api';

const handlePrintDraw = () => window.print();

const exportDrawCSV = (matches, standings, selectedEvent, getRoundName, tournamentName) => {
  const name = `${tournamentName || 'tournament'}_${selectedEvent?.name || 'draw'}`.replace(/\s+/g, '_');
  let csv = '';

  if (selectedEvent?.draw_type === 'league' && standings.length > 0) {
    // Standings table
    const sHeaders = ['Pos', 'Player', 'Played', 'Won', 'Lost', 'Points'];
    const sRows = standings.map((s, i) => [
      i + 1,
      s.player_name || s.player_display || '',
      s.played ?? '',
      s.won ?? '',
      s.lost ?? '',
      s.points ?? '',
    ]);
    csv += [sHeaders, ...sRows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    csv += '\n\n';
  }

  // Matches / fixtures
  const headers = ['Round', 'Match #', 'Player 1', 'Seed 1', 'Player 2', 'Seed 2', 'Winner', 'Score', 'Scheduled Time', 'Court'];
  const rows = matches
    .filter(m => m.player1_id || m.player2_id)
    .map(m => [
      getRoundName(m.round_number),
      m.match_number ?? '',
      m.player1_display || m.player1_name || 'TBD',
      m.player1_seed ?? '',
      m.player2_display || m.player2_name || 'TBD',
      m.player2_seed ?? '',
      m.winner_id === m.player1_id ? (m.player1_display || m.player1_name || '') :
        m.winner_id === m.player2_id ? (m.player2_display || m.player2_name || '') : '',
      m.score || '',
      m.scheduled_time ? new Date(m.scheduled_time).toLocaleString() : '',
      m.court_name || '',
    ]);
  csv += [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Knockout bracket visualization (same as original DrawTab) ─────────────────

const parseScore = (score) => {
  if (!score) return null;
  try { return typeof score === 'string' ? JSON.parse(score) : score; } catch { return null; }
};

// Returns [[p1score, p2score], ...] from score object's game1/game2/game3 fields
const extractGamePairs = (scoreData) => {
  if (!scoreData || scoreData.games === 'Walkover') return null;
  const pairs = [scoreData.game1, scoreData.game2, scoreData.game3]
    .filter(g => g && (parseInt(g.player1) > 0 || parseInt(g.player2) > 0))
    .map(g => [parseInt(g.player1) || 0, parseInt(g.player2) || 0]);
  return pairs.length > 0 ? pairs : null;
};

const ScoreGames = ({ pairs, playerIdx }) => {
  if (!pairs || pairs.length === 0) return null;
  return (
    <span style={{ marginLeft: 5, display: 'inline-flex', gap: 3, flexShrink: 0 }}>
      {pairs.map((pair, i) => {
        const mine = pair[playerIdx];
        const theirs = pair[1 - playerIdx];
        const won = mine > theirs;
        return (
          <span key={i} style={{
            fontSize: '0.68rem', fontWeight: won ? 700 : 400,
            color: won ? '#1d4ed8' : '#9ca3af',
            background: won ? '#eff6ff' : '#f3f4f6',
            border: `1px solid ${won ? '#93c5fd' : '#e5e7eb'}`,
            borderRadius: 3, padding: '1px 4px', minWidth: 18, textAlign: 'center',
          }}>
            {mine}
          </span>
        );
      })}
    </span>
  );
};

const WinnerDot = () => (
  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block', marginRight: 5, flexShrink: 0 }} />
);

const KnockoutBracket = ({ matches, getRoundName, qualifyingPlayerIds = new Set() }) => {
  const rounds = [...new Set(matches.map(m => m.round_number))];
  const sortedRounds = [...rounds].sort((a, b) => parseInt(a) - parseInt(b));
  if (sortedRounds.length === 0) return null;

  const firstRoundCount = matches.filter(m => m.round_number === sortedRounds[0]).length;
  const BASE_SLOT = 120;
  const totalHeight = firstRoundCount * BASE_SLOT;

  return (
    <div style={{ display: 'flex', overflowX: 'auto', padding: '20px 10px', background: '#fcfcfc' }}>
      {sortedRounds.map((roundNum, roundIdx) => {
        const roundMatches = matches.filter(m => m.round_number === roundNum).sort((a, b) => a.match_number - b.match_number);
        const slotHeight = roundMatches.length > 0 ? totalHeight / roundMatches.length : totalHeight;
        return (
          <React.Fragment key={roundNum}>
            {roundIdx > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', width: '40px', flexShrink: 0 }}>
                <div style={{ visibility: 'hidden', textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.85rem', margin: '0 4px 8px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {getRoundName(roundNum)}
                </div>
                <div style={{ position: 'relative', height: `${totalHeight}px` }}>
                  {roundMatches.map((m, idx) => (
                    <div key={`conn-${m.id}`} style={{ position: 'absolute', top: `${idx * slotHeight}px`, height: `${slotHeight}px`, width: '100%' }}>
                      <div style={{ position: 'absolute', left: '50%', top: '25%', bottom: '25%', borderLeft: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '50%', right: '-4px', top: '50%', borderTop: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '-4px', width: 'calc(50% + 4px)', top: '25%', borderTop: '2px solid #adb5bd' }} />
                      <div style={{ position: 'absolute', left: '-4px', width: 'calc(50% + 4px)', top: '75%', borderTop: '2px solid #adb5bd' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '220px', flexShrink: 0 }}>
              <div style={{ textAlign: 'center', padding: '8px', background: 'var(--primary-color)', color: 'white', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem', margin: '0 4px 8px' }}>
                {getRoundName(roundNum)}
              </div>
              <div style={{ position: 'relative', height: `${totalHeight}px` }}>
                {roundMatches.map((match, idx) => (
                  <div key={match.id} style={{ position: 'absolute', top: `${(idx + 0.5) * slotHeight}px`, transform: 'translateY(-50%)', left: '4px', right: '4px' }}>
                    <div style={{ width: '100%', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      {(() => {
                        const isWalkover = match.notes?.toLowerCase() === 'walkover';
                        const scoreData = parseScore(match.score);
                        const gamePairs = extractGamePairs(scoreData);
                        return (
                          <div style={{ border: match.winner_id ? '2px solid #28a745' : '1px solid #dee2e6', borderRadius: '6px', background: match.winner_id ? '#f8fff9' : 'white', overflow: 'hidden' }}>
                            {isWalkover && (
                              <div style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a', padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700, color: '#92400e', letterSpacing: '0.05em' }}>
                                W/O · WALKOVER
                              </div>
                            )}
                            <div style={{ padding: '7px 10px', borderBottom: '1px solid #f1f1f1', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: match.winner_id === match.player1_id ? 700 : 'normal', color: match.winner_id === match.player1_id ? '#15803d' : match.winner_id ? '#9ca3af' : '#333', minWidth: 0, overflow: 'hidden' }}>
                                {match.winner_id === match.player1_id && <WinnerDot />}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {match.player1_display || match.player1_name || (!match.player1_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : (match.notes?.toUpperCase().includes('BYE') ? 'BYE' : 'TBD'))}
                                </span>
                                {match.player1_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px', flexShrink: 0 }}>[{match.player1_seed}]</span> : ''}
                                {match.player1_id && qualifyingPlayerIds.has(match.player1_id) ? <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: '3px', fontWeight: 600, flexShrink: 0 }}>[Q]</span> : ''}
                              </span>
                              <ScoreGames pairs={gamePairs} playerIdx={0} />
                            </div>
                            <div style={{ padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: match.winner_id === match.player2_id ? 700 : 'normal', color: match.winner_id === match.player2_id ? '#15803d' : match.winner_id ? '#9ca3af' : '#333', minWidth: 0, overflow: 'hidden' }}>
                                {match.winner_id === match.player2_id && <WinnerDot />}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {match.player2_display || match.player2_name || (!match.player2_id && match.qualifier_slot ? `[${match.qualifier_slot}]` : (match.notes?.toUpperCase().includes('BYE') ? 'BYE' : 'TBD'))}
                                </span>
                                {match.player2_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px', flexShrink: 0 }}>[{match.player2_seed}]</span> : ''}
                                {match.player2_id && qualifyingPlayerIds.has(match.player2_id) ? <span style={{ fontSize: '0.65rem', color: '#0F766E', marginLeft: '3px', fontWeight: 600, flexShrink: 0 }}>[Q]</span> : ''}
                              </span>
                              <ScoreGames pairs={gamePairs} playerIdx={1} />
                            </div>
                            {match.match_start_time && match.match_end_time && (
                              <div style={{ padding: '3px 10px', borderTop: '1px solid #f0f0f0', background: '#f8fff9', fontSize: '0.65rem', color: '#6b7280', display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span>⏱</span>
                                <span>{Math.round((new Date(match.match_end_time) - new Date(match.match_start_time)) / 60000)} min</span>
                                <span style={{ color: '#d1d5db' }}>·</span>
                                <span>{new Date(match.match_start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}–{new Date(match.match_end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            )}
                            {match.status === 'in_progress' && match.match_start_time && (
                              <div style={{ padding: '3px 10px', borderTop: '1px solid #fde68a', background: '#fffbeb', fontSize: '0.65rem', color: '#d97706', fontWeight: 600 }}>
                                ⏱ In progress since {new Date(match.match_start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 2px' }}>
                        <span style={{ fontSize: '0.65rem', color: '#999' }}>
                          {match.notes === '3rd Place Playoff' ? '3rd Place' : `M${match.match_number}`}
                        </span>
                        <span style={{ fontSize: '0.65rem', color: '#999' }}>
                          {match.scheduled_time ? `${new Date(match.scheduled_time).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${new Date(match.scheduled_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── DrawTab ──────────────────────────────────────────────────────────────────

const DrawTab = ({ tournament }) => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [qualifyingPlayerIds, setQualifyingPlayerIds] = useState(new Set());

  // Load events for this tournament
  useEffect(() => {
    if (!tournament?.id) return;
    eventApi.list(tournament.id)
      .then(res => {
        setEvents(res.data);
        if (res.data.length > 0) setSelectedEventId(res.data[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, [tournament?.id]);

  const selectedEvent = events.find(e => e.id === selectedEventId);

  const loadMatchesForEvent = useCallback(async (eventId, drawType, hasQualifying) => {
    if (!eventId) return;
    setLoadingMatches(true);
    setMatches([]);
    setStandings([]);
    setQualifyingPlayerIds(new Set());
    try {
      const requests = [
        eventApi.getMatches(tournament.id, eventId),
        drawType === 'league' ? eventApi.getStandings(tournament.id, eventId) : Promise.resolve({ data: [] }),
        hasQualifying ? eventApi.getQualifierSlots(tournament.id, eventId) : Promise.resolve(null),
      ];
      const [mRes, sRes, qRes] = await Promise.all(requests);
      setMatches(mRes.data);
      setStandings(sRes.data);
      if (qRes) setQualifyingPlayerIds(new Set(qRes.data?.qualifyingPlayerIds || []));
    } catch {} finally { setLoadingMatches(false); }
  }, [tournament.id]);

  // Load matches whenever selected event changes
  useEffect(() => {
    if (selectedEvent) {
      loadMatchesForEvent(selectedEvent.id, selectedEvent.draw_type, !!selectedEvent.has_qualifying);
    }
  }, [selectedEvent, loadMatchesForEvent]);

  const getRoundName = (roundNumber) => {
    if (!selectedEvent || selectedEvent.draw_type === 'league') return `Round ${roundNumber}`;
    const nonPlayoff = matches.filter(m => m.notes !== '3rd Place Playoff');
    const cnt = nonPlayoff.filter(m => m.round_number === roundNumber).length;
    if (cnt === 1) return 'Final';
    if (cnt === 2) return 'Semi Finals';
    if (cnt === 4) return 'Quarter Finals';
    if (cnt === 8) return 'Round of 16';
    if (cnt === 16) return 'Round of 32';
    return `Round of ${cnt * 2}`;
  };

  const isLeague = selectedEvent?.draw_type === 'league';
  const hasDraw = matches.length > 0;

  if (loadingEvents) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  if (events.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <p>No events created yet. Go to the Events tab to add events and generate draws.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header: title + event picker + print */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>
          {isLeague ? 'League Standings' : 'Tournament Draw'}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {/* Event selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.82rem', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>Event:</label>
            <select
              value={selectedEventId}
              onChange={e => setSelectedEventId(e.target.value)}
              style={{ fontSize: '0.85rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#111827', cursor: 'pointer' }}
            >
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.name}</option>
              ))}
            </select>
          </div>
          {hasDraw && (
            <>
              <button className="btn btn-outline btn-sm no-print" onClick={() => exportDrawCSV(matches, standings, selectedEvent, getRoundName, tournament?.name)}>Export CSV</button>
              <button className="btn btn-outline btn-sm no-print" onClick={handlePrintDraw}>Print</button>
            </>
          )}
        </div>
      </div>

      {/* Event info badge */}
      {selectedEvent && (
        <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <span>{selectedEvent.event_type} · {selectedEvent.event_category}{selectedEvent.age_category && selectedEvent.age_category !== 'Open' ? ` · ${selectedEvent.age_category}` : ''}</span>
          <span style={{ color: '#d1d5db' }}>|</span>
          <span>{selectedEvent.player_count || 0}/{selectedEvent.max_participants} entries</span>
          {hasDraw && (
            <>
              <span style={{ color: '#d1d5db' }}>|</span>
              <span>{matches.filter(m => m.status === 'completed').length}/{matches.filter(m => m.player1_id && m.player2_id && !(m.notes || '').includes('BYE')).length} matches done</span>
            </>
          )}
        </div>
      )}

      {loadingMatches ? (
        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '2rem', textAlign: 'center' }}>Loading draw...</div>
      ) : !hasDraw ? (
        <div className="empty-state">
          <p>No draw generated yet for <strong>{selectedEvent?.name}</strong>. Go to the Events tab to generate the draw.</p>
        </div>
      ) : isLeague ? (
        /* ── League: standings + fixture list ── */
        <>
          {standings.length > 0 && (
            <div className="table-container" style={{ marginBottom: '1.5rem' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Player</th>
                    <th style={{ textAlign: 'center' }}>Played</th>
                    <th style={{ textAlign: 'center' }}>Won</th>
                    <th style={{ textAlign: 'center' }}>Lost</th>
                    <th style={{ textAlign: 'center' }}>Pts</th>
                    <th style={{ textAlign: 'center' }}>Sets W</th>
                    <th style={{ textAlign: 'center' }}>Sets L</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row, i) => (
                    <tr key={row.player_id} style={{ background: i === 0 ? '#f0fff4' : 'inherit' }}>
                      <td style={{ color: '#999', fontSize: '0.85rem' }}>{i + 1}</td>
                      <td style={{ fontWeight: i === 0 ? '700' : '500' }}>{row.player_name}</td>
                      <td style={{ textAlign: 'center' }}>{row.played}</td>
                      <td style={{ textAlign: 'center', color: '#28a745' }}>{row.won}</td>
                      <td style={{ textAlign: 'center', color: '#dc3545' }}>{row.lost}</td>
                      <td style={{ textAlign: 'center' }}><strong>{row.points}</strong></td>
                      <td style={{ textAlign: 'center', color: '#6c757d' }}>{row.sets_won}</td>
                      <td style={{ textAlign: 'center', color: '#6c757d' }}>{row.sets_lost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {matches.length > 0 && standings.length === 0 && (
            <div className="empty-state"><p>No matches completed yet. Standings will appear here as results are entered.</p></div>
          )}
          {/* Fixture list by round */}
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>Fixtures</h4>
            {[...new Set(matches.map(m => m.round_number))].sort((a, b) => a - b).map(roundNum => {
              const roundMatches = matches.filter(m => m.round_number === roundNum);
              return (
                <div key={roundNum} style={{ marginBottom: '1rem' }}>
                  <div style={{ fontWeight: '600', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Round {roundNum}
                  </div>
                  {roundMatches.map(match => {
                    const isBye = match.notes && match.notes.includes('BYE');
                    if (isBye) return null;
                    return (
                      <div key={match.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.25rem', background: match.winner_id ? '#f8fff9' : '#fafafa', border: match.winner_id ? '1px solid #c3e6cb' : '1px solid #e9ecef', borderRadius: '6px' }}>
                        <span style={{ fontWeight: match.winner_id === match.player1_id || match.player1_seed ? '700' : '400', color: match.winner_id === match.player1_id ? '#28a745' : '#333', flex: '1', textAlign: 'right' }}>
                          {match.player1_display || match.player1_name || 'TBD'}
                          {match.player1_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px' }}>[{match.player1_seed}]</span> : ''}
                        </span>
                        <span style={{ color: '#999', fontSize: '0.8rem', minWidth: '24px', textAlign: 'center' }}>vs</span>
                        <span style={{ fontWeight: match.winner_id === match.player2_id || match.player2_seed ? '700' : '400', color: match.winner_id === match.player2_id ? '#28a745' : '#333', flex: '1' }}>
                          {match.player2_display || match.player2_name || 'TBD'}
                          {match.player2_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', marginLeft: '3px' }}>[{match.player2_seed}]</span> : ''}
                        </span>
                        {match.winner_id && (
                          <span className="badge badge-success" style={{ fontSize: '0.7rem', marginLeft: '0.5rem' }}>
                            {match.winner_name}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        /* ── Knockout: visual bracket ── */
        <KnockoutBracket matches={matches} getRoundName={getRoundName} qualifyingPlayerIds={qualifyingPlayerIds} />
      )}
    </div>
  );
};

export default DrawTab;
